import { XMarkIcon } from '@heroicons/react/24/outline';

import { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { KeyboardShortcut } from '@/Pages/KeyboardShortcutsSidePane';
import { FeedbackDiff, useFeedbackCrud } from '@/hooks/useFeedbackCrud';
import useToastOn429Error from '@/hooks/useToastOn429Error';
import ProgressCircleIcon from '@/icons/ProgressCircleIcon.svg?react';
import { FeedbackConfigSchema, FeedbackSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../Select/Select';

type FeedbackConfigType = FeedbackConfigSchema['feedback_config']['type'];
type FilterUnion<T, U> = T extends U ? T : never;

interface FeedbackItemProps<
  Type extends FeedbackConfigType = FeedbackConfigType
> {
  config: FilterUnion<FeedbackConfigSchema['feedback_config'], { type: Type }>;
  feedback: FeedbackSchema | undefined;

  isError?: boolean;
  disabled?: boolean;
  onMutateReset?: () => void;

  onSubmit: (value: FeedbackDiff) => Promise<FeedbackSchema | undefined>;
  onDelete: () => Promise<FeedbackSchema | undefined>;
}

function ContinuousFeedback(props: FeedbackItemProps<'continuous'>) {
  const getDefaultState = (feedback: FeedbackSchema | undefined) => {
    let strValue = feedback?.score?.toFixed(4) ?? '';
    while (
      (strValue.includes(',') || strValue.includes('.')) &&
      (strValue.endsWith('0') ||
        strValue.endsWith(',') ||
        strValue.endsWith('.'))
    ) {
      strValue = strValue.slice(0, -1);
    }

    return strValue;
  };

  const ref = useRef<HTMLFormElement>(null);
  const [value, setValue] = useState(() => getDefaultState(props.feedback));

  if (props.config?.categories) {
    const categories = props.config.categories;
    const actualValue =
      !props.feedback ||
      !!categories.find((cat) => props.feedback?.score === cat.value);

    if (actualValue) {
      return (
        <CategoricalFeedback
          {...props}
          config={{
            type: 'categorical',
            categories: props.config.categories,
          }}
        />
      );
    }
  }

  return (
    <form
      ref={ref}
      className="flex justify-end"
      onSubmit={(e) => {
        e.stopPropagation();
        e.preventDefault();

        const newValue = new FormData(e.currentTarget).get('value') as
          | string
          | null;
        if (newValue != null) {
          if (newValue === '' && props.feedback) {
            props.onDelete().then(() => setValue(getDefaultState(undefined)));
          } else if (newValue !== getDefaultState(props.feedback)) {
            const score = Number.parseFloat(newValue as string);
            if (Number.isNaN(score)) return;

            props
              .onSubmit({ score })
              .then((feedback) => setValue(getDefaultState(feedback)));
          }
        }
      }}
    >
      <div
        className={cn(
          'inline-flex items-stretch divide-x divide-secondary rounded-md border border-secondary focus-within:border-brand',
          props.isError && 'divide-error border-error focus-within:border-error'
        )}
      >
        <div className="inline-grid flex-shrink-0 grid-cols-1 grid-rows-1">
          <input
            id={`input-feedback-${props.feedback?.id}`}
            placeholder="0.00"
            disabled={props.disabled}
            className={cn(
              'col-[1] row-[1] border-none bg-transparent p-[5px] px-2 text-center text-sm',
              'min-w-0 text-transparent caret-black outline-none placeholder:text-tertiary dark:caret-slate-200'
            )}
            size={4}
            name="value"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              props.onMutateReset?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                // Lose focus on the input
                e.currentTarget.blur();
              }
            }}
            onBlur={() =>
              ref.current?.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true })
              )
            }
          />
          <div
            className={cn(
              'col-[1] row-[1] border-none bg-transparent p-[5px] px-2 text-center text-sm',
              'pointer-events-none',
              props.disabled && 'opacity-50'
            )}
          >
            {value}{' '}
          </div>
        </div>

        {value && (
          <div className="flex min-h-[30px] min-w-[30px] items-center justify-center">
            <button
              type="button"
              id={`delete-feedback-${props.feedback?.id}`}
              className="rounded-sm p-1 outline-none transition-colors focus-within:bg-secondary hover:bg-secondary active:bg-secondary disabled:bg-transparent disabled:opacity-50"
              disabled={props.disabled}
              onClick={() =>
                props
                  .onDelete()
                  .then(() => setValue(getDefaultState(undefined)))
              }
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </form>
  );
}

function CategoricalFeedback(props: FeedbackItemProps<'categorical'>) {
  const [open, setOpen] = useState(false);
  const categories =
    props.config.type === 'categorical' ? props.config.categories ?? [] : [];

  const activeValue =
    props.feedback?.score != null ? props.feedback.score : null;

  const twoCategoryRef = useRef<HTMLDivElement>(null);
  const [hotkeysEnabled, setHotkeysEnabled] = useState(false);

  useHotkeys(
    'Escape',
    (event) => {
      if (open) {
        event.stopPropagation();
        setOpen(false);
      }
    },
    [open]
  );

  useHotkeys(
    '0,1',
    (event) => {
      const key = parseInt(event.key);
      const button: HTMLButtonElement | null | undefined =
        twoCategoryRef.current?.querySelector(
          `[id^="select-feedback"] button:nth-child(${key + 1})`
        );
      button?.click();
    },
    [twoCategoryRef],
    { enabled: hotkeysEnabled && categories.length <= 2 }
  );

  useEffect(() => {
    const handleFocus = (event: FocusEvent) => {
      if (
        event.target instanceof HTMLElement &&
        twoCategoryRef.current?.contains(event.target)
      ) {
        setHotkeysEnabled(true);
      } else {
        setHotkeysEnabled(false);
      }
    };

    document.addEventListener('focusin', handleFocus);

    return () => {
      document.removeEventListener('focusin', handleFocus);
    };
  }, []);

  if (categories.length > 2) {
    return (
      <div
        className={cn(
          'flex items-center divide-x divide-secondary rounded-md border border-secondary focus-within:border-brand',
          props.isError && 'divide-error border-error focus-within:border-error'
        )}
      >
        <Select
          value={activeValue?.toString() || ''}
          disabled={props.disabled}
          onValueChange={(strValue) => {
            const label = categories.find(
              (category) => category.value.toString() === strValue
            )?.label;

            props.onSubmit({ score: +strValue, value: label });
          }}
          open={open}
          onOpenChange={setOpen}
        >
          <SelectTrigger
            className="gap-1.5 border-none py-[5px] pl-2 pr-1.5"
            id={`select-feedback-${props.feedback?.id}`}
          >
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem
                key={category.value}
                value={category.value.toString()}
              >
                {category.label ?? category.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {activeValue != null ? (
          <div className="flex min-h-[30px] min-w-[30px] items-center justify-center p-0.5">
            <button
              type="button"
              id={`delete-feedback-${props.feedback?.id}`}
              className="rounded-sm p-1 outline-none transition-colors focus-within:bg-secondary hover:bg-secondary active:bg-secondary disabled:bg-transparent disabled:opacity-50"
              disabled={props.disabled}
              onClick={() => props.onDelete()}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      id={`select-feedback-${props.feedback?.id}`}
      className={cn(
        'flex items-center divide-x divide-secondary overflow-hidden rounded-md border border-secondary focus-within:border-brand',
        props.isError && 'divide-error border-error focus-within:border-error'
      )}
      tabIndex={-1}
      ref={twoCategoryRef}
    >
      {categories?.map((category, idx) => (
        <button
          type="button"
          key={category.value}
          className={cn(
            'flex min-h-[30px] min-w-[30px] items-center justify-center px-2 py-1 text-sm outline-none',
            category.value === activeValue
              ? 'bg-brand-secondary text-brand-secondary'
              : 'bg-transparent'
          )}
          disabled={props.disabled}
          onClick={() =>
            props.onSubmit({
              score: category.value,
              value: category.label,
            })
          }
        >
          {category.label ?? category.value}
          {(category.label ?? category.value) != idx.toString() && (
            <KeyboardShortcut keys={[`${idx}`]} />
          )}
        </button>
      ))}

      {activeValue != null ? (
        <div className="flex min-h-[30px] min-w-[30px] items-center justify-center">
          <button
            type="button"
            id={`delete-feedback-${props.feedback?.id}`}
            className="rounded-sm p-1 outline-none transition-colors focus-within:bg-secondary hover:bg-secondary active:bg-secondary disabled:bg-transparent disabled:opacity-50"
            disabled={props.disabled}
            onClick={() => props.onDelete()}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function RunAnnotationsSingleFeedbackForm(props: {
  runId: string;
  config: FeedbackConfigSchema;
  feedback: FeedbackSchema | undefined;
  onSuccess?: () => void;
  idx: number;
}) {
  const { is429Error } = useToastOn429Error();

  const { feedback, onSubmit, onDelete, error, reset, isMutating } =
    useFeedbackCrud({
      runId: props.runId,
      feedback_key: props.config.feedback_key,
      existingFeedback: props.feedback,
      onSuccess: props.onSuccess,
    });

  const feedbackProps = {
    feedback,
    onSubmit,
    onDelete,
    isError: !!error,
    onMutateReset: () => reset(),
    disabled: isMutating,
  } satisfies Omit<FeedbackItemProps, 'config'>;

  const feedbackRef = useRef<HTMLDivElement>(null);

  useHotkeys(
    'x',
    () => {
      const deleteButton = document.getElementById(
        `delete-feedback-${feedback?.id}`
      );
      const prefix = 'select-feedback-';
      const activeElementId = document.activeElement?.id;
      let id;
      if (activeElementId?.startsWith(prefix)) {
        id = activeElementId?.substring(prefix.length);
      }
      if (isMutating) return;
      if (feedback == null) return;
      if (id == feedback?.id) {
        deleteButton?.click();
      }
    },
    [feedback, isMutating, props.idx]
  );

  return (
    <>
      <div className="inline-flex items-center gap-2" ref={feedbackRef}>
        {isMutating && (
          <ProgressCircleIcon
            className="h-4 w-4 animate-spin text-brand-green-400"
            role="progressbar"
          />
        )}

        {props.config.feedback_config.type === 'categorical' && (
          <CategoricalFeedback
            config={props.config.feedback_config}
            {...feedbackProps}
          />
        )}

        {props.config.feedback_config.type === 'continuous' && (
          <ContinuousFeedback
            config={props.config.feedback_config}
            {...feedbackProps}
          />
        )}
      </div>

      {error && !is429Error(error) && (
        <span className="max-w-[230px] text-wrap text-right text-sm text-error">
          {error?.toString()}
        </span>
      )}
    </>
  );
}
